import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CompactDialog from "../../../../components/CompactDialog";
import CustomTextField from "../../../../components/Fields/Textfield";

// Edit is name-only by design — the symbol icon cannot be changed after creation.
interface RenameSymbolDialogProps {
  open: boolean;
  initialDescription: string;
  saving?: boolean;
  onSave: (description: string) => void;
  onCancel: () => void;
}

const RenameSymbolDialog: FC<RenameSymbolDialogProps> = ({
  open,
  initialDescription,
  saving = false,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [description, setDescription] = useState(initialDescription);

  useEffect(() => {
    if (open) {
      setDescription(initialDescription);
    }
  }, [open, initialDescription]);

  const canSave = description.trim().length > 0 && !saving;

  return (
    <CompactDialog
      open={open}
      title={t("configuration.ingenieurslegende.renameDialogTitle")}
      iconColor="primary"
      iconVariant="Bulk"
      showCloseButton={false}
      cancelText={t("common.cancel")}
      confirmText={
        saving
          ? t("configuration.ingenieurslegende.renaming")
          : t("configuration.ingenieurslegende.renameButton")
      }
      confirmColor="inherit"
      cancelButtonProps={{
        color: "error",
        startIcon: <CloseIcon fontSize="small" />,
        sx: { borderColor: alpha(theme.palette.error.main, 0.48) },
      }}
      confirmButtonProps={{
        variant: "outlined",
        startIcon: <CheckIcon fontSize="small" />,
        disabled: !canSave,
      }}
      onConfirm={() => canSave && onSave(description.trim())}
      onCancel={onCancel}
    >
      <CustomTextField
        mode="light"
        fullWidth
        label={t("configuration.ingenieurslegende.descriptionLabel")}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
    </CompactDialog>
  );
};

export default RenameSymbolDialog;
